import React, { useState, useMemo } from 'react';
import { Clock, Calendar, Activity, Edit, Quote, RotateCcw } from 'lucide-react';
import { motion } from 'framer-motion';

const QUOTES = [
  "Every moment is a fresh beginning.",
  "Life is a gift. Make the most of every day.",
  "The future depends on what you do today.",
  "Time is a treasure. Spend it wisely.",
  "Your life is shaped by your daily choices."
];

const DEFAULT_ACTIVITIES = {
  Sleep: { 
    dailyHours: 7.5,
    color: '#3B82F6',
    icon: '🌙'
  },
  Work: { 
    dailyHours: 8,
    color: '#10B981',
    icon: '💼'
  },
  PersonalCare: { 
    dailyHours: 2.5,
    color: '#F43F5E',
    icon: '🧘'
  },
  Learning: { 
    dailyHours: 1,
    color: '#8B5CF6',
    icon: '📚'
  },
  Exercise: { 
    dailyHours: 0.5,
    color: '#F97316',
    icon: '💪'
  },
  Leisure: { 
    dailyHours: 2,
    color: '#22D3EE',
    icon: '🎮'
  },
  Social: { 
    dailyHours: 1.5,
    color: '#FBBF24',
    icon: '👥'
  },
  Commute: { 
    dailyHours: 1,
    color: '#6366F1',
    icon: '🚗'
  }
};

const LifeMap = () => {
  const [dob, setDob] = useState('');
  const [age, setAge] = useState(null);
  const [lifespan, setLifespan] = useState(75);
  const [isEditingLifespan, setIsEditingLifespan] = useState(false);
  const [activities, setActivities] = useState(DEFAULT_ACTIVITIES);
  const [currentQuote, setCurrentQuote] = useState(QUOTES[0]);
  const [showInYears, setShowInYears] = useState(false);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuote(QUOTES[Math.floor(Math.random() * QUOTES.length)]);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  const handleCalculateAge = () => {
    if (!dob) return;
    const birthDate = new Date(dob);
    const today = new Date();
    const calculatedAge = today.getFullYear() - birthDate.getFullYear() -
      (today.getMonth() < birthDate.getMonth() || 
       (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate()) ? 1 : 0);
    setAge(calculatedAge);
  };

  const generateLifeHeatmap = useMemo(() => {
    if (!age) return [];
    const totalYears = lifespan;
    const years = [];
    
    for (let year = 0; year < totalYears; year++) {
      const isCompleted = year < age;
      const isCurrent = year === Math.floor(age);
      years.push({ year, isCompleted, isCurrent });
    }
    
    // Group years into rows of 10 for better visualization
    const rows = [];
    for (let i = 0; i < years.length; i += 10) {
      rows.push(years.slice(i, i + 10));
    }
    
    return rows;
  }, [age, lifespan]);

  const calculateTimeMetrics = (activity) => {
    const hoursPerYear = activity.dailyHours * 365;
    const totalSpentHours = hoursPerYear * age;
    const totalRemainingHours = hoursPerYear * (lifespan - age);
    
    return {
      spent: showInYears ? (totalSpentHours / (24 * 365)).toFixed(1) : Math.round(totalSpentHours),
      remaining: showInYears ? (totalRemainingHours / (24 * 365)).toFixed(1) : Math.round(totalRemainingHours),
      unit: showInYears ? 'years' : 'hours'
    };
  };

  const handleActivityChange = (activityName, value) => {
    setActivities(prev => ({
      ...prev,
      [activityName]: {
        ...prev[activityName],
        dailyHours: value
      }
    }));
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-4xl mx-auto p-4 sm:p-6 bg-white shadow-lg rounded-xl"
    >
      {/* Quote Section */}
      <motion.div 
        key={currentQuote}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg text-center"
      >
        <Quote className="inline-block mb-2 text-blue-500" />
        <p className="text-lg italic text-gray-700">{currentQuote}</p>
      </motion.div>

      {/* Input Section */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        
        <div className="space-y-4">
          
          <div className="flex items-center space-x-4">
            
            <Calendar className="text-blue-500" />
        
            <input 
              type="date" 
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              className="p-2 border rounded-md flex-grow"
            />
       

           <div>
            
           </div>
           
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleCalculateAge}
              className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition"
            >
              Calculate
            </motion.button>
            
          </div>
     
          <p style={{display:"flex",justifyContent:"flex-start"}} className="text-gray-500 text-sm mt-1 ">* Enter your date of birth</p>
          <div className="flex items-center space-x-2">
            <span className="font-medium">Expected Lifespan:</span>
            {isEditingLifespan ? (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center space-x-2"
              >
                <input 
                  type="number" 
                  value={lifespan}
                  onChange={(e) => setLifespan(Number(e.target.value))}
                  className="w-20 p-1 border rounded"
                  min="50"
                  max="120"
                />
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsEditingLifespan(false)}
                  className="bg-green-500 text-white px-2 py-1 rounded"
                >
                  Save
                </motion.button>
              </motion.div>
            ) : (
              <div className="flex items-center">
                <span className="mr-2 font-bold text-blue-600">{lifespan} years</span>
                <Edit 
                  className="text-blue-500 cursor-pointer" 
                  onClick={() => setIsEditingLifespan(true)} 
                  size={18} 
                />
              </div>
            )}
          </div>
        </div>

        {age && (
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-gradient-to-r from-blue-500 to-indigo-500 p-4 rounded-lg text-white"
          >
            <h2 className="text-xl font-bold mb-2">Life Overview</h2>
            <p className="text-2xl font-bold mb-1">Age: {age}</p>
            <p className="text-lg">
              Years ahead: <span className="font-bold">{Math.max(lifespan - age, 0)}</span>
            </p>
            <p className="text-sm mt-2 opacity-80">
              That's {Math.max(lifespan - age, 0) * 365} days to make a difference!
            </p>
          </motion.div>
        )}
      </div>

      {age && (
        <>
          {/* Time Toggle */}
          <div className="flex justify-end mb-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowInYears(!showInYears)}
              className="flex items-center space-x-2 bg-blue-100 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-200 transition"
            >
              <RotateCcw size={16} />
              <span>Show in {showInYears ? 'Hours' : 'Years'}</span>
            </motion.button>
          </div>

          {/* Activity Grid */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
          >
            {Object.entries(activities).map(([name, activity]) => {
              const timeMetrics = calculateTimeMetrics(activity);
              return (
                <motion.div
                  key={name}
                  whileHover={{ scale: 1.02 }}
                  className="bg-gray-50 p-4 rounded-lg"
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium flex items-center">
                      <span className="mr-2">{activity.icon}</span>
                      {name}
                    </span>
                  </div>
                  <input
                    type="number"
                    value={activity.dailyHours}
                    onChange={(e) => handleActivityChange(name, parseFloat(e.target.value) || 0)}
                    className="w-full p-2 border rounded-md"
                    min="0"
                    step="0.5"
                  />
                  <div className="mt-2 text-sm">
                    <div className="flex justify-between text-gray-600">
                      <span>Spent:</span>
                      <span className="font-medium">{timeMetrics.spent} {timeMetrics.unit}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Remaining:</span>
                      <span className="font-medium">{timeMetrics.remaining} {timeMetrics.unit}</span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Life Journey Visualization */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white p-6 rounded-lg shadow"
          >
            <h3 className="text-xl font-bold mb-4">Life Journey Visualization</h3>
            <div className="space-y-2">
              {generateLifeHeatmap.map((row, rowIndex) => (
                <div key={rowIndex} className="flex space-x-2">
                  {row.map((year) => (
                    <motion.div
                      key={year.year}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: year.year * 0.01 }}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs
                        ${year.isCompleted ? 'bg-green-500 text-white' : 
                          year.isCurrent ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'}`}
                      title={`Year ${year.year + 1}`}
                    >
                      {year.year + 1}
                    </motion.div>
                  ))}
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
                <span>Completed</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-blue-500 rounded mr-2"></div>
                <span>Current Year</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-gray-200 rounded mr-2"></div>
                <span>Future</span>
              </div>
            </div>
          </motion.div>
        </>
      )}
       <footer className="mt-auto text-center text-gray-500 text-sm py-4">
        Built by <a href="https://github.com/sanjay-xdr" className="text-blue-500 hover:underline">Sanjay</a>
      </footer>
    </motion.div>
  );
};

export default LifeMap;